import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, ShieldIcon, UploadCloudIcon, XIcon, FileIcon } from 'lucide-react';
// Maybank pinned to the top, then alphabetical
const BANKS = ['Maybank', 'Affin Bank', 'Affin Islamic Bank', 'Agrobank', 'Alliance Bank', 'AmBank', 'Bank Islam', 'Bank Muamalat', 'Bank Rakyat', 'BSN', 'CIMB Bank', 'CIMB Islamic Bank', 'Citibank', 'Hong Leong Bank', 'HSBC Bank', 'MBSB Bank', 'OCBC Bank', 'Public Bank', 'RHB Bank', 'Standard Chartered', 'UOB'];
interface SimulatedFile {
  name: string;
  size: number;
}
export function UpdatePayoutAccount() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [bank, setBank] = useState('Maybank');
  const [accountNumber, setAccountNumber] = useState('');
  const [confirmAccountNumber, setConfirmAccountNumber] = useState('');
  const [file, setFile] = useState<SimulatedFile | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const accountsMatch = accountNumber.length > 0 && accountNumber === confirmAccountNumber;
  const showMismatch = confirmAccountNumber.length > 0 && accountNumber !== confirmAccountNumber;
  const isValid = bank !== '' && accountNumber.length >= 6 && accountsMatch && file !== null;
  const goBack = () => navigate('/settings?tab=payout');
  // Simulate selecting a recent bank statement on click — no native file dialog needed
  const simulateUpload = () => {
    const today = new Date().toISOString().slice(0, 10);
    setFile({
      name: `Bank_Statement_${today}.pdf`,
      size: 2457600 // ~2.4 MB
    });
  };
  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    simulateUpload();
  };
  const handleSubmit = () => {
    if (!isValid) return;
    setSubmitting(true);
    // Persist a pending payout-account-update request so other pages can react
    const requestId = `PAYOUT-REQ-${Date.now()}`;
    const payload = {
      id: requestId,
      bank,
      accountNumber,
      maskedAccount: `••••${accountNumber.slice(-4)}`,
      fileName: file?.name,
      submittedAt: new Date().toISOString(),
      status: 'Pending'
    };
    try {
      sessionStorage.setItem('pendingPayoutUpdate', JSON.stringify(payload));
    } catch {
      // ignore storage errors
    }
    setTimeout(() => {
      setSubmitting(false);
      navigate('/settings?tab=payout', {
        state: {
          payoutUpdateSubmitted: true,
          requestId
        }
      });
    }, 600);
  };
  return <div className="min-h-full bg-gray-50" data-id="element-10221">
      <div className="max-w-3xl mx-auto px-6 py-8 pb-32" data-id="element-10222">
        {/* Back link */}
        <button onClick={goBack} className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors mb-6 group" data-id="element-10223">
          <ChevronLeftIcon size={16} className="mr-1 group-hover:-translate-x-0.5 transition-transform" data-id="element-10224" />
          Back to Payout Settings
        </button>

        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-10225">
          Update Payout Account (MYR)
        </h1>

        {/* Security notice banner */}
        <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8" data-id="element-10226">
          <ShieldIcon size={18} className="text-blue-700 flex-shrink-0 mt-0.5" data-id="element-10227" />
          <p className="text-sm text-blue-900 leading-relaxed" data-id="element-10228">
            <span className="font-semibold" data-id="element-10229">Security Notice:</span> To protect
            your funds, updating your settlement account requires a standard
            compliance review. Your current account (Maybank ****5678) will
            continue receiving payouts until the new account is approved.
          </p>
        </div>

        {/* Card 1: New Bank Details */}
        <section className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 mb-6" data-id="element-10230">
          <h2 className="text-base font-semibold text-gray-900 mb-1" data-id="element-10231">
            New Bank Details
          </h2>
          <p className="text-sm text-gray-500 mb-5" data-id="element-10232">
            Enter the bank account where you want to receive future payouts.
          </p>

          <div className="space-y-4" data-id="element-10233">
            <div data-id="element-10234">
              <label htmlFor="bank" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-10235">
                Select your bank
              </label>
              <select id="bank" value={bank} onChange={e => setBank(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400" data-id="element-10236">
                <optgroup label="Recommended" data-id="element-10237">
                  <option value="Maybank" data-id="element-10238">Maybank</option>
                </optgroup>
                <optgroup label="All banks (A–Z)" data-id="element-10239">
                  {BANKS.filter(b => b !== 'Maybank').map(b => <option key={b} value={b} data-id="element-10240">
                      {b}
                    </option>)}
                </optgroup>
              </select>
            </div>

            <div data-id="element-10241">
              <label htmlFor="account" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-10242">
                Account Number
              </label>
              <input id="account" type="text" inputMode="numeric" value={accountNumber} onChange={e => setAccountNumber(e.target.value.replace(/\D/g, ''))} onFocus={() => {
              // Demo helper: auto-fill both account fields on first focus
              if (accountNumber === '' && confirmAccountNumber === '') {
                setAccountNumber('1234567890');
                setConfirmAccountNumber('1234567890');
              }
            }} placeholder="e.g. 1234567890" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400" data-id="element-10243" />
            </div>

            <div data-id="element-10244">
              <label htmlFor="confirm-account" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-10245">
                Confirm Account Number
              </label>
              <input id="confirm-account" type="text" inputMode="numeric" value={confirmAccountNumber} onChange={e => setConfirmAccountNumber(e.target.value.replace(/\D/g, ''))} placeholder="Re-enter account number" className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 ${showMismatch ? 'border-red-400 focus:ring-red-300 focus:border-red-400' : 'border-gray-300 focus:ring-yellow-400 focus:border-yellow-400'}`} data-id="element-10246" />
              {showMismatch && <p className="mt-1.5 text-xs text-red-600" data-id="element-10247">
                  Account numbers do not match.
                </p>}
            </div>

            <p className="text-xs text-gray-500 pt-1" data-id="element-10248">
              The account name must exactly match your registered business name.
            </p>
          </div>
        </section>

        {/* Card 2: Verification Document */}
        <section className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-10249">
          <h2 className="text-base font-semibold text-gray-900 mb-1" data-id="element-10250">
            Verification Document
          </h2>
          <p className="text-sm text-gray-500 mb-5" data-id="element-10251">
            Please upload a recent bank statement or a letter of confirmation
            from your bank.
          </p>

          {!file ? <div onClick={simulateUpload} onDragOver={e => {
          e.preventDefault();
          setIsDragging(true);
        }} onDragLeave={() => setIsDragging(false)} onDrop={onDrop} className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors ${isDragging ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'}`} role="button" tabIndex={0} onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            simulateUpload();
          }
        }} data-id="element-10252">
              <UploadCloudIcon size={32} className="mx-auto text-gray-400 mb-3" data-id="element-10253" />
              <p className="text-sm font-medium text-gray-700" data-id="element-10254">
                Click to upload or drag and drop
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-10255">
                PDF, JPG, or PNG up to 10MB
              </p>
            </div> : <div className="flex items-center justify-between border border-gray-200 rounded-lg p-4 bg-gray-50" data-id="element-10256">
              <div className="flex items-center gap-3 min-w-0" data-id="element-10257">
                <div className="w-10 h-10 rounded-md bg-white border border-gray-200 flex items-center justify-center flex-shrink-0" data-id="element-10258">
                  <FileIcon size={18} className="text-gray-500" data-id="element-10259" />
                </div>
                <div className="min-w-0" data-id="element-10260">
                  <p className="text-sm font-medium text-gray-900 truncate" data-id="element-10261">
                    {file.name}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-10262">
                    {(file.size / 1024 / 1024).toFixed(2)} MB · Simulated upload
                  </p>
                </div>
              </div>
              <button onClick={() => setFile(null)} className="p-1.5 rounded-md hover:bg-gray-200 transition-colors flex-shrink-0" aria-label="Remove file" data-id="element-10263">
                <XIcon size={16} className="text-gray-500" data-id="element-10264" />
              </button>
            </div>}

          <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" className="hidden" readOnly data-id="element-10265" />

          <p className="text-xs text-gray-500 mt-4" data-id="element-10266">
            Must clearly show the bank logo, your business name, and the new
            account number. Max size: 10MB (PDF, JPG, PNG).
          </p>
        </section>
      </div>

      {/* Sticky footer */}
      <div className="fixed bottom-0 left-0 right-0 lg:left-[var(--sidebar-w,240px)] bg-white border-t border-gray-200 z-10" data-id="element-10267">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-10268">
          <button onClick={goBack} className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" data-id="element-10269">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={!isValid || submitting} className="px-5 py-2.5 text-sm font-semibold rounded-md transition-colors disabled:cursor-not-allowed" style={{
          backgroundColor: isValid && !submitting ? '#FFCC00' : '#F3F4F6',
          color: isValid && !submitting ? '#000000' : '#9CA3AF'
        }} onMouseEnter={e => {
          if (isValid && !submitting) e.currentTarget.style.backgroundColor = '#E6B800';
        }} onMouseLeave={e => {
          if (isValid && !submitting) e.currentTarget.style.backgroundColor = '#FFCC00';
        }} data-id="element-10270">
            {submitting ? 'Submitting…' : 'Submit for Review'}
          </button>
        </div>
      </div>
    </div>;
}